import type { ReactNode } from "react";
import GamesSearchShell from "@/v2-components/pages/games/search/games-search-shell";
import { useTrans } from "@/utils/useTrans";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import type { VrGamesFilterState } from "@/v2-components/pages/games/vr-games/use-vr-games-filters";

/**
 * `/games/vr/games` 검색 입력 (시안 `29942:14072` — 데스크탑·모바일이 같은 인스턴스).
 *
 * ## 왜 컴포넌트로 뺐나
 *
 * 시안에서 검색이 **브레이크포인트마다 다른 자리**에 있다:
 *
 * ```
 *   데스크탑 `29942:14072`   필터 레일 **맨 위** (레일 폭 360 을 꽉 채움)
 *   모바일   `29942:17604`   레일이 없고 머리말 아래 (272 + 필터 버튼 48)
 * ```
 *
 * 두 자리에 각각 `<input>` 을 쓰면 같은 상태를 가리키는 입력이 둘이 되고, 한쪽만 고치면
 * 조용히 갈라진다(placeholder · iOS 줌 방어 · 아이콘 위치). 폭만 호출처가 정한다.
 *
 * ## 박스는 공유 `Input` 이다 — 시안 값과 정확히 같다
 *
 * ★첫 판은 `bg-coolgray-10` 회색 채움 + `rounded-lg`(8) + `px-3.5` 로 그렸는데 **전부
 * 틀렸다.** 시안을 렌더로 보지 않고 옛 판 기억으로 그린 값이었다. 실제 시안은:
 *
 * ```
 *              첫 판(틀림)            시안 `29942:14072`        `ui/input` 기본값
 *   배경       coolgray-10           white                     bg-transparent  (같음)
 *   보더       없음                   `#dde1e6` 1px             border-coolgray-20  ✅
 *   radius     8                     **4**                     rounded         ✅
 *   좌우       14                    **16**                    px-4            ✅
 *   높이       48(h-12 고정)          48                        14+18+14+2 = 48 ✅
 *   글자       -                     14/18                     text-base       ✅
 *   placeholder -                    `#c1c7cd`(coolgray-30)    coolgray-40     ← 덮는다
 * ```
 *
 * 즉 **여섯 중 다섯이 `Input` 기본값과 일치**한다. 그래서 손으로 그리지 않고 프리미티브를
 * 쓰고 placeholder 색만 덮는다.
 *
 * ## 왜 `GamesSearchInput` 을 그대로 쓰지 않나
 *
 * 시안이 참조하는 디자인 시스템 컴포넌트는 `GamesSearchInput` 이 맞다 — 박스 표기도
 * placeholder 키(`Search for Supported Games`)도 같다. 그런데 그 컴포넌트의 입력부는
 * **`useBhapticsContents()` 를 호출하고 렌더 시 `setSearch(router.query.search)` 를 쓰고**,
 * 확정하면 같은 파일의 `useSearchHandler` 가 **`/games/search` 로 `router.replace`** 한다.
 * 탭 안 검색을 거기 묶으면 확정할 때마다 **VR 탭을 떠난다**. 그래서 **표기만** 맞춘다.
 *
 * ⚠️ 종전 주석은 이 이탈을 「그 스토어가 `/games/search` 로 push 한다」(`games.tsx` 주석의
 * 「탭 밖으로 튕긴다」)로 설명했는데, 그 스토어(`useBhapticsContents`)의 push 는 #5448 이
 * 고쳤다 — 지금 남은 경로는 위 핸들러다.
 *
 * ⚠️ **캐럿(`TriangleDownIcon`)은 넣지 않았다.** 시안에 있지만 장식이 아니라 그 컴포넌트의
 * **추천 검색어 패널 열림 표시**다(`open && "rotate-180"`, `useGameKeywordStore`).
 * 이 레일 검색은 목록을 즉시 거를 뿐 패널이 없어서, 넣으면 **아무것도 가리키지 않는
 * 회전 표시**가 된다. 옆의 세로 구분선도 캐럿과 아이콘을 가르는 것이라 함께 뺐다.
 * 추천 패널을 붙일지는 #5464 와 같은 종류의 결정이라 시안 확인이 먼저다.
 */
/**
 * ★**드롭다운은 한 벌만 마운트한다** (#5599 2단계 · 2026-09-07).
 *
 * 이 컴포넌트는 **두 슬롯**에서 렌더된다 — 데스크탑 좌측 레일(`vr-games-filter-rail`)과
 * 모바일 스티키 줄(`pages/games/vr/games.tsx`). 둘 다 CSS 로 숨기므로 **입력창이 DOM 에
 * 두 개**다(실측 2026-09-07 · 1440·390 둘 다 2개 · 폭마다 하나는 폭 0).
 *
 * 드롭다운을 여기서 무조건 그리면 **최근/인기 검색어·Steam 조회가 두 번** 나가고 DOM 이
 * 두 벌이 된다(pitfalls #53). 그래서 `slot` 을 받아 **지금 보이는 쪽에서만** 그린다.
 *
 * ⚠️ **슬롯 자체를 `useIsMobileView` 로 가르지 않는다.** 그 훅은 SSR 에서 `true` 고정이라
 * 레일을 JS 로 가리면 **데스크탑 첫 페인트에 레일이 통째로 빠진다** — 실측:
 *
 * ```
 *   CSS 게이팅(현재)          SSR HTML 에 레일 클래스 1 · 검색 input 2
 *   JS 게이팅(!isMobileView)  SSR HTML 에 레일 클래스 **0** · input 1   ← 뒤늦게 튀어나온다
 * ```
 *
 * 드롭다운은 **포커스로 열리는 클라이언트 상태**라 그 문제가 없다. 레이아웃은 CSS 로,
 * 무거운 조회는 JS 로 가른다.
 *
 * ⚠️ 입력창 자체의 중복은 남는다(스크린리더가 검색 필드를 둘로 읽는다) — 선행 결함이고
 * 위 SSR 제약 때문에 같은 게이트로 못 푼다. 별도 이슈다.
 */
export type VrSearchSlot = "rail" | "mobile";

export default function VrGamesSearch({
  state,
  className,
  slot,
  dropdown,
  open = false,
  onOpenChange,
  onSubmit,
  onClear,
}: {
  state: VrGamesFilterState;
  className?: string;
  /** 이 인스턴스가 어느 슬롯인가. 드롭다운을 그릴지 정한다. */
  slot: VrSearchSlot;
  /** 드롭다운 내용. 활성 슬롯에서만 그려진다. */
  dropdown?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * 확정 — **페이지의 `box.submit`** 을 받는다 (#5609 후속 · 2026-09-08).
   *
   * ⚠️ 종전엔 여기서 `state.setSearch` 를 **직접** 불렀다. 그러면 URL 은 바뀌는데
   * `usePcSearchBox.submit` 을 안 거쳐 **최근 검색어가 기록되지 않고 오버레이도 안
   * 닫힌다**(실측 2026-09-08: VR 모바일에서 결과 3건인데 확정해도 오버레이 유지).
   * PC 는 `onSubmit={() => submit()}` 로 그 훅을 거친다 — 같게 맞춘다.
   */
  onSubmit?: () => void;
  /**
   * X(지우기) — **`box.clear`** 를 받는다 (#5600 · 2026-09-08).
   *
   * ★**여기서도 필수다.** 선택으로 뒀더니 **레일 슬롯이 조용히 빠졌다** — 두 슬롯 중
   * 모바일에만 배선하고 데스크탑 레일은 그대로였다(2026-09-08 · 전수 grep 으로 검출).
   * 필수라야 `tsc` 가 두 자리를 다 강제한다. 종전처럼 입력값만 비우면 확정 검색어(`?q=`)가
   * 남아 새로고침에 검색이 되살아난다 — PC 와 같은 규약이라야 두 탭이 안 갈린다.
   */
  onClear: () => void;
}) {
  const { t } = useTrans(["games"]);
  const isMobileView = useIsMobileView();
  const isActiveSlot = slot === (isMobileView ? "mobile" : "rail");

  return (
    <GamesSearchShell
      className={className}
      value={state.draft}
      onValueChange={state.setDraft}
      onSubmit={() => onSubmit?.()}
      onClear={onClear}
      open={isActiveSlot && open}
      onOpenChange={(next) => onOpenChange?.(next)}
      placeholder={t("Search for Supported Games")}
    >
      {isActiveSlot ? dropdown : null}
    </GamesSearchShell>
  );
}
